// Cache utilities for reducing API latency
// Uses localStorage with TTL-based expiration

const CACHE_VERSION = "1";
const CACHE_PREFIX = `monika_v${CACHE_VERSION}_`;

/**
 * Get item from cache if valid (not expired)
 * @param {string} key - Cache key (will be prefixed)
 * @param {number} ttlMs - Time-to-live in milliseconds
 * @returns {any|null} - Cached data or null if expired/missing
 */
export const getFromCache = (key, ttlMs) => {
  try {
    const fullKey = CACHE_PREFIX + key;
    const cached = localStorage.getItem(fullKey);
    const cachedTime = localStorage.getItem(fullKey + "_time");

    if (!cached || !cachedTime) return null;

    const age = Date.now() - parseInt(cachedTime, 10);
    if (age > ttlMs) {
      // Expired - clean up
      localStorage.removeItem(fullKey);
      localStorage.removeItem(fullKey + "_time");
      return null;
    }

    return JSON.parse(cached);
  } catch (error) {
    console.warn("Cache read error:", error);
    return null;
  }
};

/**
 * Store item in cache with timestamp
 * @param {string} key - Cache key (will be prefixed)
 * @param {any} data - Data to cache (will be JSON stringified)
 */
export const setInCache = (key, data) => {
  try {
    const fullKey = CACHE_PREFIX + key;
    localStorage.setItem(fullKey, JSON.stringify(data));
    localStorage.setItem(fullKey + "_time", Date.now().toString());
  } catch (error) {
    // localStorage might be full - clear old cache entries and retry
    console.warn("Cache quota exceeded, cleaning up...");
    cleanupOldCache();

    // Try again after cleanup
    try {
      const fullKey = CACHE_PREFIX + key;
      localStorage.setItem(fullKey, JSON.stringify(data));
      localStorage.setItem(fullKey + "_time", Date.now().toString());
    } catch (retryError) {
      // Still failing - clear all Monika cache
      console.warn("Cache still full, clearing all Monika cache...");
      clearAllCache();
    }
  }
};

/**
 * Invalidate a specific cache key
 * @param {string} key - Cache key to invalidate
 */
export const invalidateCache = key => {
  const fullKey = CACHE_PREFIX + key;
  localStorage.removeItem(fullKey);
  localStorage.removeItem(fullKey + "_time");
};

/**
 * Invalidate all cache keys matching a pattern
 * @param {string} pattern - Pattern to match (e.g., "project_" to clear all project caches)
 */
export const invalidateCachePattern = pattern => {
  const fullPattern = CACHE_PREFIX + pattern;
  const keysToRemove = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(fullPattern)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    localStorage.removeItem(key + "_time");
  });
};

/**
 * Clean up expired and oldest cache entries
 */
export const cleanupOldCache = () => {
  const entries = [];
  const maxAge = 60 * 60 * 1000; // 1 hour - be more aggressive

  // Collect all cache entries with their age
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX) && !key.endsWith("_time")) {
      const timeKey = key + "_time";
      const cachedTime = localStorage.getItem(timeKey);
      const age = cachedTime ? Date.now() - parseInt(cachedTime, 10) : Infinity;
      const size = (localStorage.getItem(key)?.length || 0);
      entries.push({ key, timeKey, age, size });
    }
  }

  // Sort by age (oldest first)
  entries.sort((a, b) => b.age - a.age);

  // Remove entries older than maxAge, or at least half of all entries
  const minToRemove = Math.ceil(entries.length / 2);
  let removed = 0;

  for (const entry of entries) {
    if (entry.age > maxAge || removed < minToRemove) {
      localStorage.removeItem(entry.key);
      localStorage.removeItem(entry.timeKey);
      removed++;
    }
  }

  console.log(`Cache cleanup: removed ${removed} of ${entries.length} entries`);
};

/**
 * Clear all Monika cache entries
 */
export const clearAllCache = () => {
  const keysToRemove = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => localStorage.removeItem(key));
  console.log(`Cleared all Monika cache: ${keysToRemove.length} entries`);
};

// Cache TTL constants (in milliseconds)
export const CACHE_TTL = {
  CONTEXT: 60 * 60 * 1000, // 1 hour for S3 context
  PROJECTS_LIST: 5 * 60 * 1000, // 5 minutes for projects list
  PROJECT_DETAIL: 10 * 60 * 1000, // 10 minutes for individual project
  NAMES_LIST: 5 * 60 * 1000, // 5 minutes for names list
  PROCESS_LIST: 30 * 60 * 1000, // 30 minutes for processes
  STATS: 30 * 60 * 1000, // 30 minutes for stats
  TRADEMARK_HISTORY: 7 * 24 * 60 * 60 * 1000, // 7 days for trademark search history
};

// Cache key generators
export const CACHE_KEYS = {
  context: () => "backend_context",
  processContext: () => "process_context",
  projectsList: () => "projects_list",
  projectDetail: projectId => `project_${projectId}`,
  projectNames: (projectId, maxChars) =>
    maxChars ? `names_${projectId}_${maxChars}` : `names_${projectId}`,
  projectStats: projectId => `stats_${projectId}`,
  trademarkHistory: () => "trademark_history",
};

/**
 * Debug helper - log all cache entries to console
 */
export const debugCache = () => {
  const entries = [];
  let totalSize = 0;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX) && !key.endsWith("_time")) {
      const value = localStorage.getItem(key);
      const size = value?.length || 0;
      totalSize += size;
      const timeKey = key + "_time";
      const cachedTime = localStorage.getItem(timeKey);
      const age = cachedTime ? Math.round((Date.now() - parseInt(cachedTime, 10)) / 1000) : "?";
      entries.push({
        key: key.replace(CACHE_PREFIX, ""),
        size: `${(size / 1024).toFixed(2)} KB`,
        age: `${age}s`,
      });
    }
  }

  console.log(`Total Monika cache: ${(totalSize / 1024).toFixed(2)} KB`);
  console.table(entries);
  return entries;
};

// Make debug and clear functions available in browser console
if (typeof window !== "undefined") {
  window.debugMonikaCache = debugCache;
  window.clearMonikaCache = clearAllCache;
}
